import { baseApi as api } from "../baseApi";
export const addTagTypes = ["Orders"] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      listOrders: build.query<ListOrdersApiResponse, ListOrdersApiArg>({
        query: (queryArg) => ({
          url: `/api/v1/vendor/orders`,
          params: {
            customerId: queryArg.customerId,
            posOrderId: queryArg.posOrderId,
            customerName: queryArg.customerName,
            customerPhone: queryArg.customerPhone,
            customerEmail: queryArg.customerEmail,
            summary: queryArg.summary,
            status: queryArg.status,
            createdFrom: queryArg.createdFrom,
            createdTo: queryArg.createdTo,
            page: queryArg.page,
            size: queryArg.size,
          },
        }),
        providesTags: ["Orders"],
      }),
      createOrder: build.mutation<CreateOrderApiResponse, CreateOrderApiArg>({
        query: (queryArg) => ({
          url: `/api/v1/vendor/orders`,
          method: "POST",
          body: queryArg.createOrderForm,
        }),
        invalidatesTags: ["Orders"],
      }),
      getOrder: build.query<GetOrderApiResponse, GetOrderApiArg>({
        query: (queryArg) => ({ url: `/api/v1/vendor/orders/${queryArg.id}` }),
        providesTags: ["Orders"],
      }),
      updateOrder: build.mutation<UpdateOrderApiResponse, UpdateOrderApiArg>({
        query: (queryArg) => ({
          url: `/api/v1/vendor/orders/${queryArg.id}`,
          method: "PATCH",
          body: queryArg.updateOrderForm,
        }),
        invalidatesTags: ["Orders"],
      }),
      markOrderRead: build.mutation<
        MarkOrderReadApiResponse,
        MarkOrderReadApiArg
      >({
        query: (queryArg) => ({
          url: `/api/v1/vendor/orders/${queryArg.id}/read`,
          method: "POST",
        }),
        invalidatesTags: ["Orders"],
      }),
      sendChatLink: build.mutation<SendChatLinkApiResponse, SendChatLinkApiArg>(
        {
          query: (queryArg) => ({
            url: `/api/v1/vendor/orders/${queryArg.id}/slack-notification`,
            method: "POST",
          }),
          invalidatesTags: ["Orders"],
        },
      ),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as ordersApi };
export type ListOrdersApiResponse =
  /** status 200 OK */ PageDataVendorOrderData;
export type ListOrdersApiArg = {
  /** Only this customer's orders */
  customerId?: number;
  /** Part of the POS order ID */
  posOrderId?: string;
  /** Part of the customer's name */
  customerName?: string;
  /** Digits contained in the customer's phone; other characters are ignored */
  customerPhone?: string;
  /** Part of the customer's email */
  customerEmail?: string;
  /** Part of the summary */
  summary?: string;
  /** Any of these statuses */
  status?: (
    | "PENDING"
    | "CONFIRMED"
    | "PROCESSING"
    | "DISPATCHED"
    | "DELIVERED"
    | "CANCELLED"
  )[];
  /** Placed at or after this instant */
  createdFrom?: string;
  /** Placed before this instant (exclusive) */
  createdTo?: string;
  /** Zero-based page number */
  page?: number;
  /** Rows per page; clamped to the server maximum */
  size?: number;
};
export type CreateOrderApiResponse = /** status 201 Created */ VendorOrderData;
export type CreateOrderApiArg = {
  createOrderForm: CreateOrderForm;
};
export type GetOrderApiResponse = /** status 200 OK */ VendorOrderData;
export type GetOrderApiArg = {
  id: number;
};
export type UpdateOrderApiResponse = /** status 200 OK */ VendorOrderData;
export type UpdateOrderApiArg = {
  id: number;
  updateOrderForm: UpdateOrderForm;
};
export type MarkOrderReadApiResponse = unknown;
export type MarkOrderReadApiArg = {
  id: number;
};
export type SendChatLinkApiResponse =
  /** status 200 OK */ SlackNotificationData;
export type SendChatLinkApiArg = {
  id: number;
};
export type VendorOrderData = {
  /** The customer's private link to this order's chat */
  chatUrl?: string;
  createdAt?: string;
  customerCompany?: string;
  customerEmail?: string;
  customerId?: number;
  customerName?: string;
  customerPhone?: string;
  id?: number;
  lastMessageAt?: string;
  /** Internal note, never shown to the customer */
  notes?: string;
  posOrderId?: string;
  /** When the chat link was last sent on Slack */
  slackNotifiedAt?: string;
  status?:
    | "PENDING"
    | "CONFIRMED"
    | "PROCESSING"
    | "DISPATCHED"
    | "DELIVERED"
    | "CANCELLED";
  summary?: string;
  /** Customer messages the vendor has not read */
  unreadCount?: number;
  updatedAt?: string;
};
export type PageDataVendorOrderData = {
  hasNext?: boolean;
  hasPrevious?: boolean;
  items?: VendorOrderData[];
  page?: number;
  size?: number;
  totalItems?: number;
  totalPages?: number;
};
export type CreateOrderForm = {
  customerId: number;
  /** Internal note, never shown to the customer */
  notes?: string;
  /** The order's ID in the vendor's POS */
  posOrderId?: string;
  summary?: string;
};
export type UpdateOrderForm = {
  notes?: string;
  posOrderId?: string;
  /** When the status changes, also tell the customer in the chat */
  postStatusToChat?: boolean;
  status:
    | "PENDING"
    | "CONFIRMED"
    | "PROCESSING"
    | "DISPATCHED"
    | "DELIVERED"
    | "CANCELLED";
  summary?: string;
};
export type SlackNotificationData = {
  /** False when Slack is not configured or did not accept it */
  sent?: boolean;
  sentAt?: string;
};
export const {
  useListOrdersQuery,
  useLazyListOrdersQuery,
  useCreateOrderMutation,
  useGetOrderQuery,
  useLazyGetOrderQuery,
  useUpdateOrderMutation,
  useMarkOrderReadMutation,
  useSendChatLinkMutation,
} = injectedRtkApi;
